import { SetStateAction } from "react";
import CheerzApiProvider from "@Providers/CheerzApiProvider";
import { DispatchNotificationAction } from "@States/notifications";
import { ApiErrorResponse, ApiV2ErrorResponse } from "@Types/api/error";
import { ScreenParams } from "@Types/login";
import { checkPasswordFormat } from "@Utils/checkFormats";
import { apiV2ErrorManager, errorManager } from "@Utils/error";
import { getMetaContent } from "@Utils/meta";
import { convertToIdentifyProperties, setDataLayerTrack } from "@Utils/track";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { convertUserFromApiV2ToContent } from "@Utils/user";

const cheerzApiProvider = (locale: string): CheerzApiProvider => {
  const csrfToken = getMetaContent("csrf-token");

  if (!csrfToken) {
    throw new Error("CSRF Token not found");
  }

  return new CheerzApiProvider(locale, csrfToken, true);
};

export const logUserWithPassword = (
  params: { email: string; firstName: string; password: string },
  onChangeScreen: (newScreenParams: ScreenParams) => void,
  dispatchNotification: DispatchNotificationAction,
  setDisabled: (value: SetStateAction<boolean>) => void,
  locale: string
): void => {
  const redirection = (validatedPassword: string): void => {
    cheerzApiProvider(locale)
      .signIn(params.email, locale, validatedPassword)
      .then((data) => {
        const userConnected = convertUserFromApiV2ToContent(data.response);

        setDataLayerTrack("Identify", convertToIdentifyProperties(userConnected));
        onChangeScreen({
          action: "signIn",
          disabledAction: false,
          firstName: userConnected.firstName,
          screen: "welcome",
        });
      })
      .catch((error: ApiV2ErrorResponse) => {
        apiV2ErrorManager(error, dispatchNotification);
        setDisabled(false);
      });
  };

  checkPasswordFormat(params.password)
    .then((passwordChecked) => redirection(passwordChecked))
    .catch((errorCode: string) => {
      errorManager(dispatchNotification, errorCode);
      setDisabled(false);
    });
};

export const createAccount = (
  params: { checked: boolean; email: string; firstName: string; password: string },
  onChangeScreen: (newScreenParams: ScreenParams) => void,
  dispatchNotification: DispatchNotificationAction,
  setDisabled: (value: SetStateAction<boolean>) => void,
  locale: string
): void => {
  const redirection = (validatedPassword: string): void => {
    const label = params.checked ? "Yes checked" : "No checked";

    setDataLayerTrack("Newsletter Login Checked Data", { checked: label });
    cheerzApiProvider(locale)
      .getSettings(locale)
      .then((settingsData) => {
        return cheerzApiProvider(locale)
          .signUp(
            settingsData.response.country_code3,
            settingsData.response.currency_code3,
            params.firstName,
            params.email,
            locale,
            params.checked,
            validatedPassword
          )
          .then((data) => {
            const userConnected = convertUserFromApiV2ToContent(data.response);

            setDataLayerTrack("Identify", convertToIdentifyProperties(userConnected));
            onChangeScreen({
              action: "signUp",
              disabledAction: false,
              firstName: userConnected.firstName,
              screen: "welcome",
            });
          });
      })
      .catch((error: ApiV2ErrorResponse) => {
        apiV2ErrorManager(error, dispatchNotification);
        setDisabled(false);
      });
  };

  checkPasswordFormat(params.password)
    .then((passwordChecked) => redirection(passwordChecked))
    .catch((errorCode: string) => {
      errorManager(dispatchNotification, errorCode);
      setDisabled(false);
    });
};

export const recoverPassword = (
  email: string | undefined,
  onChangeScreen: (newScreenParams: ScreenParams) => void,
  dispatchNotification: DispatchNotificationAction,
  locale: string
): void => {
  cheerzApiProvider(locale)
    .sendRecoverPasswordEmail(email)
    .then(() =>
      onChangeScreen({
        action: "signIn",
        email,
        screen: "forgotten",
      })
    )
    .catch((error: ApiErrorResponse) => {
      errorManager(
        dispatchNotification,
        "CFE-001",
        error.response?.data?.error_description[0] ?? undefined
      );
    });
};
